'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Box, InputAdornment, Paper, TextField } from '@mui/material';
import { SearchIcon } from '@/components/icons';
import { filterCountries, findCountryByIso, type CountryOption } from '../countries';
import { isRendered } from '../../dialogFocus';
import { dialMenuHost, dialMenuPosition } from './dial-code-menu';
import {
  IntakeCountryPickerList,
  optionDomId,
  type CountryPickerMode,
} from './IntakeCountryPickerList';

type Props = {
  anchor: HTMLElement;
  mode: CountryPickerMode;
  listId: string;
  value: string;
  detectedIso: string | null;
  searchPlaceholder: string;
  searchAriaLabel: string;
  listAriaLabel: string;
  onSelect: (iso2: string) => void;
  /** When restoreFocus is true, the trigger should take focus (Escape / select). */
  onClose: (restoreFocus?: boolean) => void;
};

function flatOptions(
  detected: CountryOption | null,
  rest: CountryOption[],
  showDetected: boolean
): CountryOption[] {
  if (showDetected && detected) {
    return [detected, ...rest];
  }
  return rest;
}

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Next or previous tabbable in the same root, skipping nodes inside `exclude`. */
export function findAdjacentTabbable(
  trigger: HTMLElement,
  exclude: HTMLElement | null,
  direction: 'next' | 'prev'
): HTMLElement | null {
  const root = trigger.getRootNode();
  if (!(root instanceof Document || root instanceof ShadowRoot)) return null;
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)
  ).filter(el => {
    if (exclude?.contains(el)) return false;
    if (el.tabIndex < 0) return false;
    return isRendered(el);
  });
  const index = candidates.indexOf(trigger);
  if (index < 0) return null;
  const adjacent =
    direction === 'next' ? candidates[index + 1] : candidates[index - 1];
  return adjacent ?? null;
}

export function IntakeCountryPickerMenu({
  anchor,
  mode,
  listId,
  value,
  detectedIso,
  searchPlaceholder,
  searchAriaLabel,
  listAriaLabel,
  onSelect,
  onClose,
}: Props) {
  const [query, setQuery] = React.useState('');
  const [pos, setPos] = React.useState(() => dialMenuPosition(anchor));
  const [activeIso, setActiveIso] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const host = dialMenuHost(anchor);
  const matchDialCode = mode === 'dial';
  const detected = findCountryByIso(detectedIso);
  const filtered = filterCountries(query, { matchDialCode });
  const showDetected = Boolean(
    detected && filtered.some(c => c.iso2 === detected.iso2)
  );
  const rest = showDetected
    ? filtered.filter(c => c.iso2 !== detected?.iso2)
    : filtered;
  const options = React.useMemo(
    () => flatOptions(showDetected ? detected : null, rest, showDetected),
    // rest identity changes each render; key by ISO list + detected
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showDetected, detected?.iso2, rest.map(o => o.iso2).join(',')]
  );
  const optionKey = options.map(o => o.iso2).join(',');
  const activeDescendant =
    activeIso && options.some(o => o.iso2 === activeIso)
      ? optionDomId(listId, activeIso)
      : undefined;

  React.useLayoutEffect(() => {
    searchRef.current?.focus();
  }, []);

  React.useLayoutEffect(() => {
    const selected = value.toUpperCase();
    const preferred =
      options.find(o => o.iso2 === selected)?.iso2 ?? options[0]?.iso2 ?? null;
    setActiveIso(preferred);
  }, [optionKey, value, options]);

  React.useLayoutEffect(() => {
    if (!activeIso || !listRef.current) return;
    const node = listRef.current.querySelector(
      `#${CSS.escape(optionDomId(listId, activeIso))}`
    );
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIso, listId, optionKey]);

  React.useLayoutEffect(() => {
    const update = () => setPos(dialMenuPosition(anchor));
    update();
    const root = anchor.getRootNode();
    root.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      root.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor]);

  React.useEffect(() => {
    const root = anchor.getRootNode();
    function onPointer(event: Event) {
      // composedPath sees through the shadow boundary (event.target is
      // retargeted to the widget host once the event leaves the ShadowRoot).
      const path = event.composedPath();
      const target = path[0];
      if (
        !(target instanceof Node) ||
        (menuRef.current && path.includes(menuRef.current)) ||
        path.includes(anchor)
      ) {
        return;
      }
      // A click on a focusable control keeps that focus; anywhere else the
      // trigger gets it back instead of the dialog container.
      const focusable = target instanceof Element && target.closest(TABBABLE_SELECTOR);
      // Leave host-page clicks alone (text selection, native focus, etc.).
      const insideWidget = root.contains(target);
      if (!focusable && insideWidget) event.preventDefault();
      onClose(!focusable);
    }
    function onKey(event: Event) {
      if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;
      // Drawer listens on window and closes the chat on Escape inside the widget.
      event.stopPropagation();
      // Escape that cancels IME composition belongs to the IME: keep the
      // default action (ends composition) and keep the popup open, but still
      // stop the event so it cannot reach the drawer. Safari reports the
      // keydown that ends composition with isComposing=false and keyCode 229.
      if (event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      onClose(true);
    }
    // Window + capture for both: pointer events on the host page never enter
    // the widget's ShadowRoot, and Escape must win wherever focus is (even
    // outside the shadow root, e.g. a screen-reader cursor), so neither
    // reaches the drawer or the host page while the popup is open.
    window.addEventListener('mousedown', onPointer, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onPointer, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [anchor, onClose]);

  function moveActive(delta: number) {
    if (options.length === 0) return;
    const current = activeIso
      ? options.findIndex(o => o.iso2 === activeIso)
      : -1;
    const next = Math.max(0, Math.min(options.length - 1, current + delta));
    setActiveIso(options[next].iso2);
  }

  function onSearchKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (options[0]) setActiveIso(options[0].iso2);
        break;
      case 'End':
        event.preventDefault();
        if (options.length > 0) setActiveIso(options[options.length - 1].iso2);
        break;
      case 'Enter':
        event.preventDefault();
        if (activeIso) onSelect(activeIso);
        break;
      case 'Tab': {
        // Move focus before unmounting so React does not drop it with the search field.
        event.preventDefault();
        const adjacent = findAdjacentTabbable(
          anchor,
          menuRef.current,
          event.shiftKey ? 'prev' : 'next'
        );
        adjacent?.focus();
        onClose(false);
        break;
      }
      default:
        break;
    }
  }

  if (!host) return null;

  return createPortal(
    <Paper
      ref={menuRef}
      elevation={8}
      sx={{
        // Launcher drawer uses the max z-index; a lower value paints behind the panel.
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: 320,
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1, flexShrink: 0 }}>
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          placeholder={searchPlaceholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          slotProps={{
            htmlInput: {
              role: 'combobox',
              'aria-label': searchAriaLabel,
              'aria-autocomplete': 'list',
              'aria-expanded': true,
              'aria-controls': listId,
              'aria-activedescendant': activeDescendant,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <IntakeCountryPickerList
        listId={listId}
        mode={mode}
        listAriaLabel={listAriaLabel}
        detected={showDetected ? detected : null}
        options={rest}
        value={value}
        activeIso={activeIso}
        listRef={listRef}
        onSelect={onSelect}
      />
    </Paper>,
    host
  );
}
